import {
    ADDITIONAL_ATTRIBUTION_PARAMS,
    ATTRIBUTION_PARAM_KEYS,
} from '@lib/analytics/constants'
import escapeRegExp from 'lodash/escapeRegExp'
import { useSearchParams } from 'next/navigation'
import type { ReadonlyURLSearchParams } from 'next/navigation'
import type { NextRouter } from 'next/router'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'

type Query = NextRouter['query']

const STEP_HOST = 'step.com'

/**
 * Additional attribution parameter prefixes not in ATTRIBUTION_PARAM_KEYS.
 * These are checked as prefixes (e.g., 'af_' matches 'af_c_id', 'af_siteid', etc.)
 */
const ATTRIBUTION_PARAM_PREFIXES = ['af_']

/**
 * Check if a URL parameter key is an attribution parameter that should be preserved.
 */
const isAttributionParam = (key: string): boolean => {
    // Check exact matches from analytics constants
    if (ATTRIBUTION_PARAM_KEYS.includes(key)) return true

    // Check additional exact matches
    if (ADDITIONAL_ATTRIBUTION_PARAMS.includes(key)) return true

    // Check prefix matches (e.g., af_*)
    if (ATTRIBUTION_PARAM_PREFIXES.some((prefix) => key.startsWith(prefix)))
        return true

    return false
}

/**
 * Parse a URL string into components using the modern URL API.
 * Handles both absolute URLs and relative paths.
 */
const parseUrl = (urlString: string) => {
    try {
        // Try parsing as absolute URL first
        const url = new URL(urlString)
        const query: Record<string, string | string[]> = {}
        url.searchParams.forEach((value, key) => {
            const existing = query[key]
            if (existing) {
                query[key] = Array.isArray(existing)
                    ? [...existing, value]
                    : [existing, value]
            } else {
                query[key] = value
            }
        })
        return {
            protocol: url.protocol.replace(':', ''),
            host: url.host,
            pathname: url.pathname,
            query,
            hash: url.hash,
        }
    } catch {
        // If it fails, treat as relative URL
        const url = new URL(urlString, 'https://example.com')
        const query: Record<string, string | string[]> = {}
        url.searchParams.forEach((value, key) => {
            const existing = query[key]
            if (existing) {
                query[key] = Array.isArray(existing)
                    ? [...existing, value]
                    : [existing, value]
            } else {
                query[key] = value
            }
        })
        return {
            protocol: undefined,
            host: undefined,
            pathname: url.pathname,
            query,
            hash: url.hash,
        }
    }
}

/**
 * Format URL components into a URL string using the modern URL API.
 */
const formatUrl = ({
    protocol,
    host,
    pathname,
    query,
}: {
    protocol?: string
    host?: string
    pathname?: string
    query?: Record<string, any>
}) => {
    const base = host
        ? `${protocol || 'https'}://${host}`
        : 'https://example.com'
    const url = new URL(pathname || '/', base)

    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((v) =>
                        url.searchParams.append(key, String(v)),
                    )
                    return
                }
                url.searchParams.set(key, String(value))
            }
        })
    }

    if (host) {
        return url.href
    }
    return url.pathname + url.search + url.hash
}

/**
 * Allows for easier derivative URL creation with or without host/query params.
 * Preserves UTM params by default.
 */
export class LocalLink {
    static APP_REGEX = /^(\/)(app|client|webview)\/?/

    protocol = 'https'
    host: string
    hash: string
    pathname: string
    route: string
    query: Query

    constructor({ base, host, query, route, hash }: URLParts) {
        const parsedBase = parseUrl(base)
        this.host = host ?? parsedBase.host ?? STEP_HOST
        this.pathname = removeLeadingSlash(parsedBase.pathname ?? '')
        this.hash = hash ?? ''
        this.route = route ?? this.pathname
        this.query = { ...parsedBase.query, ...query }
    }

    /**
     * Assigns query params to the url. Will replace existing values in case of dupes
     */
    addQueryParams(query: Query): LocalLink {
        this.query = { ...this.query, ...query }
        return this
    }

    /**
     * Assigns the host to the url.
     */
    setHostAndProtocol(host: string, protocol: string): LocalLink {
        this.host = host
        this.protocol = protocol
        return this
    }

    /** Serializes to string.
     *
     * __WARNING__: Trailing slashes WILL BE REMOVED to avoid duplication by search-engines.
     *
     * Please do not change this behaviour without:
     * - carefully considering your reasons for doing it;
     * - consulting SEO people;
     * - creating 301 redirects for all pages.
     */
    serialize(
        router?: { asPath: string; query: ParsedUrlQuery },
        {
            preserveFullQuery,
            preserveUTM = true,
            preserveAttribution = preserveUTM,
            canonical = false,
        }: SerializeOptions = {},
    ): string {
        const { host, pathname, hash, protocol } = this

        const query = {}

        if (router) {
            for (const [key, value] of Object.entries(
                parseUrl(router.asPath).query,
            )) {
                if (
                    preserveFullQuery ||
                    (preserveAttribution && isAttributionParam(key))
                ) {
                    ;(query as any)[key] = value
                }
            }
        }

        Object.assign(query, this.query)

        // `canonical` should be used for search engine readable links, which
        // don't need the query and hash
        // Additionally, our robots.txt disallows pages with query params and
        // hash only works in browsers
        if (canonical) {
            return removeLeadingSlash(
                formatUrl({
                    host,
                    pathname,
                    protocol,
                    query: preserveFullQuery ? query : {},
                }),
            )
        }

        return addAnchor(
            removeLeadingSlash(formatUrl({ pathname, query })),
            hash,
        )
    }

    asNextProps(router: NextRouter): {
        href: string
        active: boolean
        as?: string
    } {
        const { route, pathname } = this

        const active = pathname === router.asPath

        const serialized = this.serialize(router)

        if (route) return { href: route, as: serialized, active }
        return { href: serialized, active }
    }

    asRegExp(): RegExp {
        return new RegExp(escapeRegExp(this.serialize()))
    }
}

/**
 * Determines if the given URL is a local route.
 */
export const isLocal = (base: string): boolean => {
    const { host } = parseUrl(base)
    return !host || host === STEP_HOST || host === `www.${STEP_HOST}`
}

/**
 * Determines if the given URL is rendered in a WebView inside the Step app.
 */
export const isAppPage = (
    pathname: string,
    params?: ReadonlyURLSearchParams,
) => {
    const isShownInApp = params?.get('shown-in-app') != null
    return isShownInApp || LocalLink.APP_REGEX.test(pathname)
}

/**
 * Hook that determines if the given URL is rendered in a WebView inside the Step app.
 */
export const useIsAppPage = (pathname: string) => {
    const router = useRouter()
    const params = useSearchParams()
    return isAppPage(router.isReady ? router.asPath : pathname, params)
}

/**
 * Removes /app and /client from the pathname
 */
export const stripAppPrefix = (pathname: string): string => {
    return pathname.replace(LocalLink.APP_REGEX, '$1')
}

/**
 * Appends optional anchor to the path. Anchor may or may not contain the leading #.
 * Preserves query params when adding the anchor.
 */
export const addAnchor = (path: string, anchor?: string): string => {
    if (!anchor) return path
    // Remove any existing hash from path before adding new anchor
    const pathWithoutHash = path.split('#')[0]
    const normalizedAnchor = anchor.startsWith('#') ? anchor : `#${anchor}`
    return pathWithoutHash + normalizedAnchor
}

/**
 * Removes leading slash from a path/url.
 */
export const removeLeadingSlash = (s: string) => s.replace(/\/+$/, '') || '/'

export interface URLParts {
    /** Base URL. Relative and absolute URLs with or without query params and protocol. */
    base: string
    /** Next.js route, used for dynamic links. If not set, pathname will be used */
    route?: string
    /** Overwrite query with this object. You can use this or concat stringified query to base param */
    query?: NextRouter['query']
    /** New hash to set. */
    hash?: string
    /** New host to set. Setting this option will result in a canonical URL. */
    host?: string
}

export interface SerializeOptions {
    /** Set this to merge current browser query with what you passed, otherwise it will be overwritten. */
    preserveFullQuery?: boolean
    /**
     * @deprecated Use preserveAttribution instead. Kept for backwards compatibility.
     * Set this to false not to preserve UTM params such as campaign, when overwriting query.
     */
    preserveUTM?: boolean
    /**
     * Preserve all attribution params (UTM, gclid, fbclid, pid, af_*, ttclid, ScCid, etc.).
     * Defaults to true (or the value of preserveUTM for backwards compatibility).
     */
    preserveAttribution?: boolean
    /** Set this to true to serialize to Canonical URL with host and without GET-params.  */
    canonical?: boolean
}
